import { Box, Grid, Typography } from "@mui/material";
import { DateTime } from "luxon";
import React, { useContext } from "react";

import { SessionContext } from "../../../Context/SessionContext";
import { DisqualReason } from "../../../Utils/Enums";
import { heightFeetValidation, heightInchesValidation, weightValidation } from "../../../Utils/ValidationSchema";
import DatePicker from "../../Helpers/DatePicker";
import { ValidatedTextField } from "../../Helpers/ValidatedTextField";
import StepperItem from "../StepperItem";

const Qualifying: React.FC = () => {
  const { customer, leadDisqualifiers, unverifiedCustomer, setCustomer, setUnverifiedCustomer } =
    useContext(SessionContext);

  const bmiNonCandidate = leadDisqualifiers.some(d => d.name === DisqualReason.BMI);

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <StepperItem>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography variant="stageDirection">
                Address the following questions naturally through the flow of the conversation.
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2">
                To help us prepare for your consultation, could you provide your height and weight?
              </Typography>
            </Grid>
          </Grid>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Grid container item spacing={1}>
            <Grid item>
              <ValidatedTextField
                label="Feet"
                value={unverifiedCustomer.feet}
                onChange={feet => setUnverifiedCustomer(c => ({ ...c, feet: feet ?? "" }))}
                onAccepted={feet =>
                  setCustomer(c => ({
                    ...c,
                    height:
                      customer.height?.inches || feet
                        ? { inches: customer.height?.inches ?? 0, feet: Number(feet || 0) }
                        : null
                  }))
                }
                sx={{ maxWidth: "8rem" }}
                {...heightFeetValidation}
              />
            </Grid>
            <Grid item>
              <ValidatedTextField
                label="Inches"
                value={unverifiedCustomer.inches}
                onChange={inches => setUnverifiedCustomer(c => ({ ...c, inches: inches ?? "" }))}
                onAccepted={inches =>
                  setCustomer(c => ({
                    ...c,
                    height:
                      customer.height?.feet || inches
                        ? { feet: customer.height?.feet ?? 0, inches: Number(inches || 0) }
                        : null
                  }))
                }
                sx={{ maxWidth: "8rem" }}
                {...heightInchesValidation}
              />
            </Grid>
          </Grid>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Grid item>
            <ValidatedTextField
              label="Weight"
              value={unverifiedCustomer.weight}
              onChange={weight => setUnverifiedCustomer(c => ({ ...c, weight: weight ?? "" }))}
              onAccepted={weight => setCustomer(c => ({ ...c, weight: weight !== undefined ? Number(weight) : null }))}
              sx={{ maxWidth: "12rem" }}
              {...weightValidation}
            />
          </Grid>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Typography variant="body2" component="span">
            {"BMI: "}
          </Typography>
          <Typography variant="body2" color={bmiNonCandidate ? "error" : undefined} component="span">
            <Box fontWeight={bmiNonCandidate ? "bold" : undefined} component="span">
              {customer.bmi || ""}
            </Box>
          </Typography>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography variant="body2">And your date of birth?</Typography>
            </Grid>
            <Grid item>
              <DatePicker
                componentsProps={{ actionBar: { actions: ["clear"] } }}
                inputProps={{ placeholder: "mm/dd/yyyy", fullWidth: true, sx: { maxWidth: "12rem" } }}
                label="Date of Birth"
                value={unverifiedCustomer.dateOfBirth}
                onChange={dateOfBirth => setUnverifiedCustomer(c => ({ ...c, dateOfBirth }))}
                onAccepted={date => setCustomer(c => ({ ...c, dateOfBirth: date?.toISO() || null }))}
              />
            </Grid>
          </Grid>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Typography variant="body2" component="span">
            Age:{" "}
            <i>
              {customer.dateOfBirth
                ? Math.floor(DateTime.now().diff(DateTime.fromISO(customer.dateOfBirth), "years").years)
                : ""}
            </i>
          </Typography>
        </StepperItem>
      </Grid>
    </Grid>
  );
};

export default Qualifying;
